import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Command, FileText, Users, Building2, Brain, X } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Input } from '../ui/input';
import { searchAPI } from '../../lib/api';
import { debounce } from '../../lib/utils';

export function CommandSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const performSearch = useCallback(
    debounce(async (q) => {
      if (q.length < 2) {
        setResults(null);
        return;
      }

      setLoading(true);
      try {
        const response = await searchAPI.search(q);
        setResults(response.data);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    performSearch(query);
  }, [query, performSearch]);

  const handleSelect = (type, item) => {
    setOpen(false);
    setQuery('');
    setResults(null);

    switch (type) {
      case 'stories':
        navigate(`/stories/${item.id}`);
        break;
      case 'clients':
        navigate(`/clients/${item.id}`);
        break;
      case 'contacts':
        navigate(`/audiences?contact=${item.id}`);
        break;
      case 'intelligence':
        navigate(`/intelligence/${item.id}`);
        break;
      default:
        break;
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'stories':
        return FileText;
      case 'clients':
        return Building2;
      case 'contacts':
        return Users;
      case 'intelligence':
        return Brain;
      default:
        return Search;
    }
  };

  const hasResults = results && Object.values(results).some((arr) => arr.length > 0);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-text-tertiary bg-bg-tertiary border border-border-default rounded-lg hover:border-border-subtle transition-colors"
        data-testid="global-search-btn"
      >
        <Search className="w-4 h-4" />
        <span>Search...</span>
        <kbd className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-surface-card rounded border border-border-default">
          <Command className="w-3 h-3" />K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl p-0 bg-surface-card border-border-default">
          <div className="flex items-center px-4 border-b border-border-default">
            <Search className="w-5 h-5 text-text-tertiary mr-3" />
            <Input
              placeholder="Search stories, clients, contacts, intelligence..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="border-0 bg-transparent focus-visible:ring-0 text-text-primary placeholder:text-text-tertiary"
              data-testid="global-search-input"
              autoFocus
            />
            <button
              onClick={() => setOpen(false)}
              className="p-1 hover:bg-border-default rounded"
            >
              <X className="w-4 h-4 text-text-tertiary" />
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto py-2">
            {loading && (
              <div className="px-4 py-8 text-center text-text-tertiary">
                Searching...
              </div>
            )}

            {!loading && query.length < 2 && (
              <div className="px-4 py-8 text-center text-text-tertiary">
                Type at least 2 characters to search
              </div>
            )}

            {!loading && query.length >= 2 && !hasResults && (
              <div className="px-4 py-8 text-center text-text-tertiary">
                No results found
              </div>
            )}

            {!loading && hasResults && (
              <div className="space-y-4">
                {Object.entries(results).map(([type, items]) => {
                  if (!items.length) return null;
                  const Icon = getIcon(type);

                  return (
                    <div key={type}>
                      <div className="px-4 py-1.5 text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                        {type}
                      </div>
                      {items.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleSelect(type, item)}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-border-default transition-colors text-left"
                          data-testid={`search-result-${item.id}`}
                        >
                          <Icon className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-text-primary truncate">
                              {item.headline || item.name || item.title}
                            </p>
                            {item.email && (
                              <p className="text-xs text-text-tertiary truncate">{item.email}</p>
                            )}
                            {item.status && (
                              <span className="text-xs text-text-tertiary capitalize">{item.status}</span>
                            )}
                            {item.type && (
                              <span className="text-xs text-text-tertiary capitalize">{item.type}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CommandSearch;
